import { useState, useEffect, useCallback } from 'react';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import { formatNoteDate, toInputDate, todayInputDate } from './dateUtils';
import ConfirmDialog from './ConfirmDialog';

const FirmNotes = ({ firmId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' | 'edit'
  const [formId, setFormId] = useState(null);
  const [formText, setFormText] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchNotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await callUIAPI(`bo/firms/${firmId}/notes`, null, apiConstants.METHOD_GET, true);
      setNotes(response.data.list || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load notes.');
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => {
    if (firmId) fetchNotes();
  }, [firmId, fetchNotes]);

  const openAdd = () => {
    setFormMode('add');
    setFormId(null);
    setFormText('');
    setFormDate(todayInputDate());
    setFormError(null);
    setFormVisible(true);
  };

  const openEdit = (note) => {
    setFormMode('edit');
    setFormId(note.id);
    setFormText(note.note || '');
    setFormDate(toInputDate(note.dateEntered));
    setFormError(null);
    setFormVisible(true);
  };

  const closeForm = () => {
    if (formSaving) return;
    setFormVisible(false);
    setFormError(null);
  };

  const handleSave = async () => {
    if (!formText.trim() || !formDate) return;
    try {
      setFormSaving(true);
      setFormError(null);
      const body = { note: formText.trim(), dateEntered: formDate };
      if (formMode === 'edit' && formId) {
        await callUIAPI(`bo/firms/${firmId}/notes/${formId}`, body, apiConstants.METHOD_PUT, true);
      } else {
        await callUIAPI(`bo/firms/${firmId}/notes`, body, apiConstants.METHOD_POST, true);
      }
      setFormVisible(false);
      fetchNotes();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to save note.');
    } finally {
      setFormSaving(false);
    }
  };

  const openDeleteDialog = (noteId) => {
    setDeleteId(noteId);
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setShowDeleteDialog(false);
    setDeleteId(null);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setDeleteError(null);
      await callUIAPI(`bo/firms/${firmId}/notes/${deleteId}`, null, apiConstants.METHOD_DELETE, true);
      setShowDeleteDialog(false);
      setDeleteId(null);
      fetchNotes();
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete note.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="firm-notes-section">
      <div className="firm-notes-header">
        <h3 className="firm-field-label">Notes</h3>
        <button className="firm-btn firm-btn-save" onClick={openAdd}>Add Note</button>
      </div>

      {formVisible && (
        <div className="firm-note-form">
          <div className="firm-note-form-row">
            <label className="firm-note-form-label" htmlFor="firm-note-date">Date</label>
            <input
              id="firm-note-date"
              className="firm-note-form-date"
              type="date"
              value={formDate}
              onChange={(e) => setFormDate(e.target.value)}
            />
          </div>
          <div className="firm-note-form-row">
            <label className="firm-note-form-label" htmlFor="firm-note-text">Note</label>
            <textarea
              id="firm-note-text"
              className="firm-note-form-textarea"
              rows={3}
              value={formText}
              onChange={(e) => setFormText(e.target.value)}
              placeholder="Enter note…"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Escape') closeForm();
              }}
            />
          </div>
          {formError && <p className="firm-field-error">{formError}</p>}
          <div className="firm-note-form-actions">
            <button
              className="firm-btn firm-btn-save"
              onClick={handleSave}
              disabled={formSaving || !formText.trim() || !formDate}
            >
              {formSaving
                ? 'Saving…'
                : formMode === 'edit'
                  ? 'Update Note'
                  : 'Save Note'}
            </button>
            <button className="firm-btn firm-btn-secondary" onClick={closeForm} disabled={formSaving}>Cancel</button>
          </div>
        </div>
      )}

      {loading && !notes.length && (
        <div className="firm-notes-loading">Loading notes…</div>
      )}
      {error && (
        <p className="firm-field-error">{error}</p>
      )}
      {!loading && !error && notes.length === 0 && (
        <p className="firm-notes-empty">No notes yet.</p>
      )}
      {notes.length > 0 && (
        <div className="firm-notes-list">
          {notes.map((note) => (
            <div key={note.id} className="firm-note-item">
              <div className="firm-note-item-header">
                <span className="firm-note-date">
                  {formatNoteDate(note.dateEntered)}
                  {(note.createdByFirstName || note.createdByLastName) && (
                    <span className="firm-note-author">
                      {' — '}{`${note.createdByFirstName ?? ''} ${note.createdByLastName ?? ''}`.trim()}
                    </span>
                  )}
                </span>
                <div className="firm-note-item-actions">
                  <button
                    className="firm-btn firm-btn-secondary firm-btn-sm"
                    onClick={() => openEdit(note)}
                  >
                    Edit
                  </button>
                  <button
                    className="firm-btn firm-btn-clear firm-btn-sm"
                    onClick={() => openDeleteDialog(note.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <p className="firm-note-text">{note.note}</p>
            </div>
          ))}
        </div>
      )}

      {}
      <ConfirmDialog
        open={showDeleteDialog}
        onClose={closeDeleteDialog}
        onConfirm={handleDelete}
        title="Delete this note?"
        icon="⚠"
        iconVariant="warning"
        confirmLabel="Delete note"
        confirmVariant="danger"
        loading={deleting}
        loadingText="Deleting note…"
        loadingSubtext={null}
        error={deleteError}
      >
        <p className="firm-dialog-body">
          This will permanently remove this note. This action cannot be undone.
        </p>
      </ConfirmDialog>
    </div>
  );
};

export default FirmNotes;